import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Flex, Image, Text } from '@chakra-ui/react';

import SearchInputGroup from '../../../../../components/SearchInputGroup';
import LoadingSpinner from '../../../../../components/Spinner';
import { analyticsIds } from '../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../constants/cypressIds';
import emptyFolderImage from '../../../../../empty-folder.png';
import { createErrorToast, createSuccessToast, createToast } from '../../../../../redux/modules/app';
import { selectCompanyId } from '../../../../../redux/modules/company';
import { hideModal } from '../../../../../redux/modules/modal';
import { globalRequirementTemplateBuilder } from '../../../../../redux/reducer';
import StarterTemplateRow from '../../StarterTemplateRow';
import TemplatesFilter from '../../TemplatesFilter';

const NoTemplatesMessage = ({ emptySearch }) => {
  const description = emptySearch
    ? 'No templates match your search.'
    : 'No published templates. To use this functionality, publish a draft '
      + 'template or create a new template by going back to the previous step and clicking "Start from Scratch."';

  return (
    <Flex alignItems="center" direction="column" height="100%" justifyContent="center" width="100%">
      <Image src={emptyFolderImage} width="250px" />
      <Text mt={4} textStyle="bodyLg500">{description}</Text>
    </Flex>
  );
};

const TemplatesListModal = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const currentCompanyId = useSelector(selectCompanyId);
  const isLoading = useSelector(globalRequirementTemplateBuilder.selectAllPublishedTemplatesLoading);

  useEffect(() => {
    dispatch(globalRequirementTemplateBuilder.listAllPublishedTemplates());
  }, []); // eslint-disable-line

  const {
    data,
    lastPage,
    total = 0,
    searchFilter
  } = useSelector(globalRequirementTemplateBuilder.selectPublishedRequirementTemplates);

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingSpinner isInline minHeight="300px" />
      );
    }

    if (data.length) {
      return (
        <InfiniteScroll
          dataLength={total}
          hasMore={data.length < total}
          loader={<LoadingSpinner isInline />}
          next={() => fetchNextData()}
        >
          {data.map(template => (
            <Box
              data-analytics-id={analyticsIds.templatesPage.createTemplateModal
                .selectTemplateModal.selectTemplateRow}
              data-cy={CypressIds.reqTemplatesSelectTemplateRowByName(template.name)}
              key={template.id}
              style={{
                textDecoration: 'none',
                _hover: { textDecoration: 'none' }
              }}
              onClick={onUseTemplateClick(template)}
            >
              <StarterTemplateRow blueprint={globalRequirementTemplateBuilder} key={template.id} template={template} />
            </Box>
          ))}
        </InfiniteScroll>
      );
    }

    return (<NoTemplatesMessage emptySearch={searchTerm} />);
  };

  const fetchNextData = () => {
    if (currentPage < lastPage) {
      const pageData = {
        pageNumber: (currentPage + 1),
        pageSize: 25
      };

      setCurrentPage(currentPage + 1);
      dispatch(globalRequirementTemplateBuilder.listAllPublishedTemplates(pageData));
    }
  };

  const onUseTemplateClick = template => async () => {
    dispatch(createToast({
      title: 'Creating template...',
      status: 'info'
    }));

    const response = await dispatch(globalRequirementTemplateBuilder.createRequirementTemplate({
      name: template.name,
      description: template.description,
      requirement_template_folder_id: null,
      creator_id: template.creatorId,
      requirement_template_blocks: template.requirementTemplateBlocks,
      published_at: null,
      updated_at: null
    }));

    if (response?.id) {
      dispatch(hideModal());
      await dispatch(globalRequirementTemplateBuilder.getRequirementTemplate(response.id));
      dispatch(createSuccessToast({ title: 'Template created!' }));
      history.replace(`/companies/${currentCompanyId}/create-templates/requirement-templates/${response.id}`);
    } else {
      dispatch(createErrorToast({
        title: 'Uh oh, something went wrong.',
        description: 'Please try again or contact support'
      }));

      dispatch(hideModal());
    }
  };

  return (
    <Box height="440px" width="100%">
      <Flex justifyContent="center" mb={6} mx="auto" width="80%">
        <SearchInputGroup
          cypressId={CypressIds.reqTemplatesSearchInputGroup}
          mr={4}
          searchTerm={searchFilter}
          width="650px"
          onChange={({ target: { value } }) => {
            setSearchTerm(value);
            dispatch(globalRequirementTemplateBuilder.updatePublishedTemplateSearchFilter(value));
          }}
        />
        <TemplatesFilter blueprint={globalRequirementTemplateBuilder} filterScope="starterTemplates" />
      </Flex>
      <Box height="85%" overflow="auto">
        {renderContent()}
      </Box>
    </Box>
  );
};

export default TemplatesListModal;
